import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Briefcase, Clock, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge, ProjectStatus } from "@/components/ProjectBadges";
import { formatDate, formatDateRange } from "@/lib/date-utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  usePersonBookedProjects,
  usePersonFunctionAssignments,
  PersonFunctionAssignment,
  PersonBookedProject,
} from "@/hooks/usePersonBookings";
import { formatProjectName } from "@/lib/format-project-name";

interface PersonBookingsProps {
  personId: string;
}

interface GroupedFunctions {
  project: {
    id: string;
    name: string;
    number: string | null;
    event_start_date: string | null;
    event_end_date: string | null;
    start_date: string | null;
    end_date: string | null;
    status: string | null;
  };
  functions: PersonFunctionAssignment[];
}

type BookingItem =
  | {
      type: "assigned";
      key: string;
      sortDate: string | null;
      group: GroupedFunctions;
    }
  | {
      type: "booked-only";
      key: string;
      sortDate: string | null;
      booking: PersonBookedProject;
    };

function formatTime(time: string | null): string {
  if (!time) return "";
  return time.substring(0, 5);
}

function getYear(date: string | null | undefined) {
  if (!date) return null;
  const year = new Date(date).getFullYear();
  return Number.isNaN(year) ? null : year;
}

function getProjectDateRange(project: GroupedFunctions["project"]) {
  return {
    start: project.event_start_date ?? project.start_date,
    end: project.event_end_date ?? project.end_date,
  };
}

function getSortDate(project: GroupedFunctions["project"], dates: string[]) {
  const { start } = getProjectDateRange(project);
  return start ?? dates[0] ?? null;
}

function getItemYears(item: BookingItem) {
  const years = new Set<number>();
  const addYear = (date: string | null | undefined) => {
    const year = getYear(date);
    if (year) years.add(year);
  };

  if (item.type === "assigned") {
    const { project, functions } = item.group;
    addYear(project.event_start_date);
    addYear(project.event_end_date);
    addYear(project.start_date);
    addYear(project.end_date);
    functions.forEach((assignment) =>
      addYear(assignment.project_function.function_date),
    );
  } else {
    const { project } = item.booking;
    addYear(project.event_start_date);
    addYear(project.event_end_date);
    addYear(project.start_date);
    addYear(project.end_date);
  }

  return years;
}

export function PersonBookings({ personId }: PersonBookingsProps) {
  const [yearFilter, setYearFilter] = useState("all");
  const { data: bookedProjects, isLoading: loadingProjects } =
    usePersonBookedProjects(personId);
  const { data: assignments, isLoading: loadingAssignments } =
    usePersonFunctionAssignments(personId);

  const isLoading = loadingProjects || loadingAssignments;

  // Group function assignments by project
  const groupedByProject = useMemo(() => {
    const groups: GroupedFunctions[] = [];
    const projectMap = new Map<string, GroupedFunctions>();
    for (const assignment of assignments ?? []) {
      const projectId = assignment.project_function.project_id;
      if (!projectMap.has(projectId)) {
        projectMap.set(projectId, {
          project: assignment.project_function.project,
          functions: [],
        });
      }
      projectMap.get(projectId)!.functions.push(assignment);
    }
    // Sort functions within each project by date
    for (const group of projectMap.values()) {
      group.functions.sort((a, b) =>
        a.project_function.function_date.localeCompare(
          b.project_function.function_date,
        ),
      );
      groups.push(group);
    }
    return groups;
  }, [assignments]);

  // Collect project IDs that appear in function assignments
  const assignedProjectIds = useMemo(
    () => new Set(groupedByProject.map((g) => g.project.id)),
    [groupedByProject],
  );

  // Projects where booked but no function assignments yet
  const bookedOnlyProjects = useMemo(
    () =>
      bookedProjects?.filter((bp) => !assignedProjectIds.has(bp.project.id)) ??
      [],
    [assignedProjectIds, bookedProjects],
  );

  const bookingItems = useMemo(() => {
    const items: BookingItem[] = [
      ...groupedByProject.map((group) => {
        const functionDates = group.functions.map(
          (assignment) => assignment.project_function.function_date,
        );
        return {
          type: "assigned" as const,
          key: group.project.id,
          sortDate: getSortDate(group.project, functionDates),
          group,
        };
      }),
      ...bookedOnlyProjects.map((booking) => ({
        type: "booked-only" as const,
        key: booking.id,
        sortDate:
          booking.project.event_start_date ??
          booking.project.start_date ??
          booking.project.end_date ??
          null,
        booking,
      })),
    ];

    return items.sort((a, b) => {
      if (!a.sortDate && !b.sortDate) return a.key.localeCompare(b.key);
      if (!a.sortDate) return 1;
      if (!b.sortDate) return -1;
      return b.sortDate.localeCompare(a.sortDate);
    });
  }, [bookedOnlyProjects, groupedByProject]);

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    bookingItems.forEach((item) => {
      getItemYears(item).forEach((year) => years.add(year));
    });
    return [...years].sort((a, b) => b - a);
  }, [bookingItems]);

  const filteredBookingItems = useMemo(() => {
    if (yearFilter === "all") return bookingItems;
    const selectedYear = Number(yearFilter);
    return bookingItems.filter((item) => getItemYears(item).has(selectedYear));
  }, [bookingItems, yearFilter]);

  const hasBookings =
    bookedOnlyProjects.length > 0 || groupedByProject.length > 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasBookings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No bookings yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-lg">Bookings</CardTitle>
        {availableYears.length > 0 && (
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger
              className="w-full sm:w-[160px]"
              aria-label="Filter bookings by year"
            >
              <Filter className="mr-2 h-4 w-4 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All years</SelectItem>
              {availableYears.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {filteredBookingItems.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No bookings match this year.
          </p>
        )}
        {filteredBookingItems.map((item) => {
          if (item.type === "assigned") {
            const { group } = item;
            const { start, end } = getProjectDateRange(group.project);
            return (
              <div
                key={item.key}
                data-testid="booking-project-card"
                className="rounded-lg border p-4 space-y-3"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Link
                    to={`/projects/${group.project.id}`}
                    className="flex items-center gap-2 hover:underline font-medium text-foreground"
                  >
                    <Briefcase className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    {group.project.number && (
                      <span className="text-sm text-muted-foreground font-mono">
                        {group.project.number}
                      </span>
                    )}
                    <span>
                      {formatProjectName(
                        group.project.name,
                        group.project.event_start_date,
                      )}
                    </span>
                  </Link>
                  <div className="flex items-center gap-2">
                    {group.project.status && (
                      <StatusBadge
                        status={group.project.status as ProjectStatus}
                      />
                    )}
                    {(start || end) && (
                      <span className="text-xs text-muted-foreground">
                        {formatDateRange(start, end)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5 pl-6">
                  {group.functions.map((assignment) => {
                    const fn = assignment.project_function;
                    return (
                      <div
                        key={assignment.id}
                        className="flex items-center gap-3 text-sm flex-wrap"
                      >
                        <span className="flex items-center gap-1 text-muted-foreground min-w-[90px]">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(fn.function_date)}
                        </span>
                        {(fn.start_time || fn.end_time) && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            {formatTime(fn.start_time)}
                            {fn.start_time && fn.end_time ? " - " : ""}
                            {formatTime(fn.end_time)}
                          </span>
                        )}
                        <span className="text-foreground">{fn.name}</span>
                        {assignment.remark && (
                          <span className="text-muted-foreground italic">
                            ({assignment.remark})
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          const bp = item.booking;
          return (
            <div
              key={item.key}
              data-testid="booking-project-card"
              className="rounded-lg border p-4"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Link
                  to={`/projects/${bp.project.id}`}
                  className="flex items-center gap-2 hover:underline font-medium text-foreground"
                >
                  <Briefcase className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  {bp.project.number && (
                    <span className="text-sm text-muted-foreground font-mono">
                      {bp.project.number}
                    </span>
                  )}
                  <span>
                    {formatProjectName(
                      bp.project.name,
                      bp.project.event_start_date,
                    )}
                  </span>
                </Link>
                <div className="flex items-center gap-2">
                  {bp.project.status && (
                    <StatusBadge status={bp.project.status as ProjectStatus} />
                  )}
                  {(bp.project.start_date || bp.project.end_date) && (
                    <span className="text-xs text-muted-foreground">
                      {formatDateRange(
                        bp.project.start_date,
                        bp.project.end_date,
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
